import { useState, useEffect } from 'react';
import { UI_CONFIG, getResponsiveValue } from '../config/ui-config';

// Custom hook for managing UI configuration
export const useUIConfig = () => {
  const [config, setConfig] = useState(UI_CONFIG);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load configuration from localStorage on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('ui-config');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig(parsedConfig);
      } catch (error) {
        setConfig(UI_CONFIG);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save configuration to localStorage whenever it changes
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('ui-config', JSON.stringify(config));
    }
  }, [config, isLoaded]);

  // Update configuration
  const updateConfig = (section, element, property, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [element]: {
          ...prev[section][element],
          [property]: {
            ...prev[section][element][property],
            ...value
          }
        }
      }
    }));
  };

  // Update responsive configuration
  const updateResponsiveConfig = (section, element, breakpoint, property, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [element]: {
          ...prev[section][element],
          responsive: {
            ...prev[section][element].responsive,
            [breakpoint]: {
              ...prev[section][element].responsive?.[breakpoint],
              [property]: {
                ...prev[section][element].responsive?.[breakpoint]?.[property],
                ...value
              }
            }
          }
        }
      }
    }));
  };

  // Get element configuration
  const getElementConfig = (section, element) => {
    return config[section]?.[element] || null;
  };

  // Get responsive configuration for current breakpoint
  const getResponsiveConfig = (section, element, breakpoint) => {
    const elementConfig = getElementConfig(section, element);
    if (!elementConfig) return null;
    
    return getResponsiveValue(elementConfig, breakpoint);
  };

  // Reset configuration to default
  const resetConfig = () => {
    setConfig(UI_CONFIG);
    localStorage.removeItem('ui-config');
  };

  // Export configuration
  const exportConfig = () => {
    const dataStr = JSON.stringify(config, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ui-config.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Import configuration
  const importConfig = (configData) => {
    try {
      const parsedConfig = typeof configData === 'string' ? JSON.parse(configData) : configData;
      setConfig(parsedConfig);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Generate CSS classes for an element
  const generateElementCSS = (section, element) => {
    const elementConfig = getElementConfig(section, element);
    if (!elementConfig) return '';

    const className = `${section}-${element}`;
    // Use more specific selectors to override existing styles
    let css = `.${className}, div.${className}, section .${className} {\n`;
    
    // Add position styles
    if (elementConfig.position) {
      Object.entries(elementConfig.position).forEach(([key, value]) => {
        css += `  ${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value} !important;\n`;
      });
    }
    
    // Add size styles
    if (elementConfig.size) {
      Object.entries(elementConfig.size).forEach(([key, value]) => {
        css += `  ${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value} !important;\n`;
      });
    }
    
    // Add transform
    if (elementConfig.transform) {
      css += `  transform: ${elementConfig.transform} !important;\n`;
    }
    
    css += `}\n\n`;
    
    // Add responsive styles
    if (elementConfig.responsive) {
      Object.entries(elementConfig.responsive).forEach(([breakpoint, responsiveConfig]) => {
        css += `@media (max-width: ${breakpoint}) {\n`;
        css += `  .${className}, div.${className}, section .${className} {\n`;
        
        if (responsiveConfig.position) {
          Object.entries(responsiveConfig.position).forEach(([key, value]) => {
            css += `    ${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value} !important;\n`;
          });
        }
        
        if (responsiveConfig.size) {
          Object.entries(responsiveConfig.size).forEach(([key, value]) => {
            css += `    ${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value} !important;\n`;
          });
        }
        
        css += `  }\n`;
        css += `}\n\n`;
      });
    }
    
    return css;
  };

  // Generate all CSS
  const generateAllCSS = () => {
    let css = `/* UI Config Styles - Generated at ${new Date().toISOString()} */\n\n`;
    
    // Add a global override to ensure our styles take precedence
    css += `/* Force UI config styles to override existing styles */\n`;
    css += `[data-ui-config="true"] {\n`;
    css += `  all: initial !important;\n`;
    css += `}\n\n`;
    
    Object.entries(config).forEach(([sectionName, sectionConfig]) => {
      if (typeof sectionConfig === 'object' && sectionConfig !== null) {
        Object.entries(sectionConfig).forEach(([elementName, elementConfig]) => {
          if (elementConfig.position || elementConfig.size) {
            css += generateElementCSS(sectionName, elementName);
          }
        });
      }
    });
    
    return css;
  };

  // Apply CSS to document
  const applyCSS = () => {
    const css = generateAllCSS();
    const styleId = 'ui-config-styles';
    
    // Remove existing style element
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Create new style element with higher specificity
    const style = document.createElement('style');
    style.id = styleId;
    style.setAttribute('data-ui-config', 'true');
    style.setAttribute('data-timestamp', Date.now().toString());
    style.textContent = css;
    
    // Add to head with high priority
    document.head.appendChild(style);
    
    // Force multiple reflows to ensure styles are applied
    document.body.offsetHeight;
    document.documentElement.offsetHeight;
    
    // Force style recalculation
    const elements = document.querySelectorAll('[class*="hero-"], [class*="features-"], [class*="about-"], [class*="services-"]');
    elements.forEach(el => {
      el.style.display = 'none';
      el.offsetHeight; // Force reflow
      el.style.display = '';
    });
  };

  return {
    config,
    isLoaded,
    updateConfig,
    updateResponsiveConfig,
    getElementConfig,
    getResponsiveConfig,
    resetConfig,
    exportConfig,
    importConfig,
    generateElementCSS,
    generateAllCSS,
    applyCSS
  };
};

export default useUIConfig;
